import React, { useState, useEffect, useContext, useRef } from "react";
import { API } from "aws-amplify";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import Link from "@material-ui/core/Link";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import CircularProgress from "@material-ui/core/CircularProgress";
import Alert from "@material-ui/lab/Alert";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SearchIcon from "@material-ui/icons/Search";
import EmailIcon from "@material-ui/icons/Email";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import logger from "../logger";
import { formatCelular } from "../functions";
import { AppContext } from "../views/App";

const useStyles = makeStyles(theme => ({
  backButton: {
    marginRight: theme.spacing(2)
  },
  searchButton: {
    marginLeft: theme.spacing(2)
  },
  filterButton: {
    marginLeft: theme.spacing(2)
  },
  title: {
    flexGrow: 1
  },
  container: {
    height: "calc(100vh - 56px)",
    [`${theme.breakpoints.up("xs")} and (orientation: landscape)`]: {
      height: "calc(100vh - 48px)"
    },
    [theme.breakpoints.up("sm")]: {
      height: "calc(100vh - 64px)"
    },
    overflow: "auto"
  },
  secondaryAction: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center"
  },
  empty: {
    minHeight: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4)
  },
  dialog: {
    transform: "translateY(-100%)"
  }
}));

const initialData = {
  contactos: [],
  lista: []
};

const fetchContactos = async ({ cuenta, onStart, onEnd }) => {
  onStart();
  const contactos = [];

  try {
    const init = {
      queryStringParameters: {
        cuenta: cuenta.id_cuenta
      }
    };

    const result = await API.get(process.env.REACT_APP_HUB_API, "/contactos", init);

    if (result.success) {
      for (let i = 0; i < result.data.length; i++) {
        const item = result.data[i];
        const body_contacto = JSON.parse(item.body_contacto);

        contactos.push({ ...item, body_contacto });
      }
    }
  } catch (err) {
    logger("ClientesList.fetchContactos.error", err);
  } finally {
    onEnd();
    return { contactos };
  }
};

const ClientesList = ({ onSelect, onBack }) => {
  const classes = useStyles();
  const { cuenta } = useContext(AppContext);
  const [openSearch, setOpenSearch] = useState(false);
  const [fetching, setFetching] = useState(null);
  const [data, setData] = useState(initialData);
  const [selected, setSelected] = useState(null);
  const [searchText, setSearchText] = useState(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const fetch = async () => {
      const { contactos } = await fetchContactos({
        cuenta,
        onStart: () => setFetching(true),
        onEnd: () => setFetching(false)
      });

      setData({
        contactos,
        lista: [...contactos]
      });
    };

    if (cuenta) {
      fetch();
    }
  }, [cuenta]);

  useEffect(() => {
    onSelect(selected);
  }, [selected, onSelect]);

  useEffect(() => {
    if (searchText) {
      const text = searchText.toLowerCase();

      const lista = data.contactos.filter(
        c =>
          c.nombre_contacto.toLowerCase().includes(text) ||
          c.body_contacto.email.toLowerCase().includes(text) ||
          c.body_contacto.telefono.toLowerCase().includes(text)
      );

      setData(s => ({ ...s, lista }));
    } else {
      setData(s => ({ ...s, lista: s.contactos }));
    }
  }, [searchText, data.contactos]);

  const openSearchDialog = () => {
    setOpenSearch(true);
  };

  const closeSearchDialog = () => {
    searchRef.current.value = "";
    setOpenSearch(false);
  };

  const resetSearch = () => {
    setSearchText(null);
  };

  const search = () => {
    const searchText = searchRef.current.value;

    if (searchText) {
      closeSearchDialog();
      setSelected(null);
      setSearchText(searchText);
    }
  };

  const searchKeyDown = event => {
    if (event.keyCode === 13) {
      event.preventDefault();
      search();
    }
  };

  const select = contacto => {
    setSelected(contacto);
  };

  const SearchDialog = () => {
    return (
      <Dialog
        fullWidth
        maxWidth="sm"
        PaperProps={{ className: classes.dialog }}
        open={openSearch}
        onClose={closeSearchDialog}
      >
        <DialogTitle>Buscar clientes</DialogTitle>

        <DialogContent>
          <DialogContentText>Ingrese el email, teléfono o el nombre del cliente.</DialogContentText>
          <TextField
            fullWidth
            autoFocus
            margin="dense"
            color="primary"
            inputProps={{
              ref: searchRef
            }}
            onKeyDown={searchKeyDown}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={closeSearchDialog} color="primary">
            Cancelar
          </Button>
          <Button onClick={search} color="primary">
            Buscar
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <div>
      <SearchDialog />

      <AppBar position="sticky">
        <Toolbar>
          {onBack && (
            <IconButton
              className={classes.backButton}
              edge="start"
              color="inherit"
              onClick={onBack}
            >
              <ArrowBackIcon />
            </IconButton>
          )}

          <Typography className={classes.title} variant="h6">
            Clientes
          </Typography>

          <IconButton
            className={classes.searchButton}
            edge="end"
            color="inherit"
            onClick={openSearchDialog}
          >
            <SearchIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <div className={classes.container}>
        {fetching ? (
          <div className={classes.empty}>
            <CircularProgress color="primary" />
          </div>
        ) : (
          <>
            {searchText && (
              <Alert
                severity="info"
                action={
                  <Button color="inherit" size="small" onClick={resetSearch}>
                    Cancelar
                  </Button>
                }
              >
                Búsqueda activa "{searchText}"
              </Alert>
            )}
            {data.lista.length === 0 ? (
              <div className={classes.empty}>
                <Typography color="textSecondary">No hay contactos</Typography>
              </div>
            ) : (
              <Paper square>
                <List disablePadding>
                  {data.lista.map((contacto, i) => {
                    return (
                      <ListItem
                        key={`contacto-${i}`}
                        button
                        divider
                        selected={selected && contacto.id_contacto === selected.id_contacto}
                        onClick={() => select(contacto)}
                      >
                        <ListItemText
                          primary={contacto.nombre_contacto}
                          secondary={
                            <>
                              {formatCelular(contacto.body_contacto.telefono)}
                              <br />
                              {contacto.body_contacto.email}
                            </>
                          }
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                        <ListItemSecondaryAction>
                          <div className={classes.secondaryAction}>
                            <Link href={`mailto:${contacto.body_contacto.email}`}>
                              <IconButton>
                                <EmailIcon />
                              </IconButton>
                            </Link>

                            <Link
                              href={`https://api.whatsapp.com/send?phone=569${contacto.body_contacto.telefono}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <IconButton>
                                <WhatsAppIcon />
                              </IconButton>
                            </Link>

                            <ChevronRightIcon color="primary" />
                          </div>
                        </ListItemSecondaryAction>
                      </ListItem>
                    );
                  })}
                </List>
              </Paper>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ClientesList;
